// BL-2437 (ADR 066 — Offline Mode § 3, ADR 033 — Transition Service): the batch check the phone
// answers while offline. It reports the one action an offline status menu offers, Draft ↔ Upcoming,
// judged by the server's own `setStatusVerdict` and role overlay. Every other action is absent,
// which the status menu reads as unavailable.
import type { AuctionPhaseKind, LotPhaseKind } from '@auctogon/core/isomorphic/lifecycle';
import type { BatchCheckResponse, BatchIneligibleRecord, BatchSetStatusCheck } from '@auctogon/core-admin/schemas/responses/lot-batch';

import { countPhaseKinds, uniformReason } from '../lots/batch-check-summary';
import { setStatusVerdict } from '../lots/set-status-verdict';

/** One cached lot, as far as the check needs it. */
export interface OfflineCheckLot {
  id: string;
  phaseKind: LotPhaseKind | null;
  auctionId: string | null;
}

/** The inputs of {@link buildOfflineSetStatusCheck}. */
export interface OfflineSetStatusCheckInput {
  /** The selected lots found on the device (an id not on the device is dropped, as the server drops a foreign id). */
  lots: OfflineCheckLot[];
  /** The cached phase of an auction, from the last sync. */
  auctionPhaseKindOf: (auctionId: string) => AuctionPhaseKind | null;
  /** The cached zone of an auction. */
  auctionTimeZoneOf: (auctionId: string) => string | null;
  /** Whether the viewer's role may run `set-status` (`batchActionPermissions('set-status')`). */
  viewerMayRunSetStatus: boolean;
}

function targetEntry(
  target: 'draft' | 'ready',
  input: OfflineSetStatusCheckInput,
): BatchSetStatusCheck['targets'][number] {
  const ineligible: BatchIneligibleRecord[] = [];
  for (const lot of input.lots) {
    const verdict = setStatusVerdict({
      lotPhaseKind: lot.phaseKind,
      isHomeLot: lot.auctionId == null,
      auctionPhaseKind: lot.auctionId == null ? null : input.auctionPhaseKindOf(lot.auctionId),
      target,
    });
    // The server's overlay: a lot-axis reason (a remedy) wins; role-action covers the rest.
    if (!verdict.allowed) ineligible.push({ id: lot.id, reason: verdict.reason });
    else if (!input.viewerMayRunSetStatus) ineligible.push({ id: lot.id, reason: 'role-action' });
  }
  const blockedReason = uniformReason(ineligible);
  return {
    target,
    available: input.lots.length > 0 && ineligible.length === 0,
    ...(blockedReason ? { blockedReason } : {}),
    ineligible,
  };
}

/** The one zone every lot's auction runs in, or null. */
function soleZone(input: OfflineSetStatusCheckInput): string | null {
  const zones = new Set(input.lots.map((lot) => (lot.auctionId == null ? null : input.auctionTimeZoneOf(lot.auctionId))));
  if (zones.size !== 1) return null;
  return [...zones][0] ?? null;
}

/**
 * Answer `POST /api/lots/batch-check` from the device.
 *
 * @param input - See {@link OfflineSetStatusCheckInput}.
 * @returns The check, with the `set-status` entry only.
 */
export function buildOfflineSetStatusCheck(input: OfflineSetStatusCheckInput): BatchCheckResponse {
  return {
    recordCount: input.lots.length,
    actions: [{ action: 'set-status', targets: [targetEntry('draft', input), targetEntry('ready', input)] }],
    hasSoldRecords: input.lots.some((lot) => lot.phaseKind === 'sold'),
    auctionTimeZone: soleZone(input),
    listedCount: input.lots.filter((lot) => lot.auctionId != null).length,
    phaseCounts: countPhaseKinds(input.lots.map((lot) => lot.phaseKind)),
  };
}
